import { displayDALocation } from '../lib/daDisplay';
import { useEffect } from 'react';
import { GMAP_KEY } from '../lib/constants';
import { hasValidCoords } from '../lib/utils';
import { getPipelineCost } from '../lib/pipelineCost';
import { buildAuthUrl, storeShareRedirect } from '../lib/deepLinks';

function formatCost(cost) {
  if (!cost || cost === 0) return null;
  return `$${Number(cost).toLocaleString()}`;
}

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

const STATUS_COLOURS = {
  'Under Assessment': { bg: 'rgba(85,128,204,0.15)', c: '#5580cc' },
  'On Exhibition':    { bg: 'rgba(212,168,74,0.15)',  c: '#d4a84a' },
  'Approved':         { bg: 'rgba(42,157,90,0.15)',   c: '#2a9d5a' },
  'Determined':       { bg: 'rgba(42,157,90,0.15)',   c: '#2a9d5a' },
  'Submitted':        { bg: 'rgba(99,102,241,0.15)',  c: '#818cf8' },
  'Rejected':         { bg: 'rgba(232,80,80,0.15)',   c: '#e85050' },
  'Withdrawn':        { bg: 'rgba(107,114,128,0.15)', c: '#9ca3af' },
};
function statusStyle(s) {
  return STATUS_COLOURS[s] || { bg: 'rgba(107,114,128,0.15)', c: '#9ca3af' };
}

export default function DAPublicPreview({ da, appNumber, council }) {
  const shareUrl  = `https://sitescopeapp.com/da?app=${encodeURIComponent(appNumber)}&council=${encodeURIComponent(council)}`;
  const location = displayDALocation(da, 'Street address not provided');
  const cost      = formatCost(getPipelineCost(da));
  const ss        = statusStyle(da.status);
  const desc      = da.description ? da.description.slice(0, 120) + (da.description.length > 120 ? '…' : '') : null;
  const hasMap    = hasValidCoords(da) && GMAP_KEY;
  const staticMap = hasMap
    ? `https://maps.googleapis.com/maps/api/staticmap?center=${da.latitude},${da.longitude}&zoom=15&size=600x200&markers=color:0xe8a830|${da.latitude},${da.longitude}&key=${GMAP_KEY}`
    : null;
  const ogImage = hasMap
    ? `https://maps.googleapis.com/maps/api/staticmap?center=${da.latitude},${da.longitude}&zoom=15&size=1200x630&markers=color:0xe8a830|${da.latitude},${da.longitude}&key=${GMAP_KEY}`
    : null;

  const titleStr = `DA ${appNumber} — ${location}`;
  const descStr  = `${da.status || ''}${cost ? ` — ${cost}` : ''}${desc ? ` — ${desc}` : ''}. Track this DA on SiteScope.`;

  // Set document title + meta tags for social sharing
  useEffect(() => {
    document.title = `${titleStr} | SiteScope`;

    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', value);
    };

    setMeta('name', 'description', descStr);
    setMeta('property', 'og:title', titleStr);
    setMeta('property', 'og:description', descStr);
    setMeta('property', 'og:url', shareUrl);
    setMeta('property', 'og:type', 'website');
    if (ogImage) setMeta('property', 'og:image', ogImage);

    return () => { document.title = 'SiteScope'; };
  }, [titleStr, descStr, shareUrl, ogImage]);

  const goToAuth = (isSignup) => {
    const redirectPath = window.location.pathname + window.location.search;
    storeShareRedirect(redirectPath, isSignup ? { appNumber, council } : null);
    window.location.href = buildAuthUrl(isSignup ? 'signup' : 'signin', redirectPath);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0f1a',
      fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
      padding: '24px 16px 48px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>

      {/* ── Logo ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, alignSelf: 'flex-start', maxWidth: 600, width: '100%' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: 'linear-gradient(135deg,#d4a84a,#d4782f)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 700, color: '#0a0f1a', fontFamily: 'Georgia,serif',
        }}>S</div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#e8e0d8', fontFamily: 'Georgia,serif', letterSpacing: 0.4 }}>SiteScope</div>
          <div style={{ fontSize: 10, color: '#9ca3af', letterSpacing: '1.2px', textTransform: 'uppercase' }}>See It First.</div>
        </div>
      </div>

      {/* ── DA summary card ────────────────────────────────────────────────── */}
      <div style={{
        width: '100%', maxWidth: 600,
        background: '#111827',
        border: '1px solid #1e2538',
        borderRadius: 12,
        overflow: 'hidden',
        marginBottom: 20,
      }}>
        {/* Static map */}
        {staticMap && (
          <img
            src={staticMap}
            alt={`Map showing ${location}`}
            style={{ width: '100%', height: 180, objectFit: 'cover', display: 'block', borderBottom: '1px solid #1e2538' }}
            loading="lazy"
          />
        )}

        <div style={{ padding: '18px 20px' }}>
          {/* DA number + status */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 12 }}>
            <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", color: '#9ca3af' }}>
              DA {appNumber}
            </div>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
              background: ss.bg, color: ss.c, whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {da.status}
            </span>
          </div>

          {/* Address */}
          {location && (
            <div style={{ fontSize: 17, fontWeight: 700, color: '#e8e0d8', marginBottom: 10, lineHeight: 1.3 }}>
              {location}
            </div>
          )}

          {/* Cost + application type */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
            {cost && (
              <div>
                <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Est. Cost</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#d4a84a' }}>{cost}</div>
              </div>
            )}
            {da.lodged_date && (
              <div>
                <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Lodged</div>
                <div style={{ fontSize: 13, color: '#e8e0d8' }}>{formatDate(da.lodged_date)}</div>
              </div>
            )}
          </div>

          {/* Description */}
          {desc && (
            <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.6 }}>
              {desc}
            </div>
          )}
        </div>
      </div>

      {/* ── CTA section ────────────────────────────────────────────────────── */}
      <div style={{
        width: '100%', maxWidth: 600,
        background: '#111827',
        border: '1px solid #1e2538',
        borderRadius: 12,
        padding: '24px 20px',
        marginBottom: 20,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#e8e0d8', marginBottom: 8, lineHeight: 1.3 }}>
          Track this DA and get status updates
        </div>
        <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.6, marginBottom: 20 }}>
          Explore development applications across Byron, Ballina, Lismore and Tweed.
        </div>
        <button
          onClick={() => goToAuth(true)}
          style={{
            width: '100%', padding: '13px',
            background: '#d4a84a', color: '#0a0f1a',
            border: 'none', borderRadius: 8,
            fontSize: 15, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'inherit', marginBottom: 12,
          }}
        >
          Get started
        </button>
        <button
          onClick={() => goToAuth(false)}
          style={{
            background: 'none', border: 'none',
            color: '#9ca3af', fontSize: 13, cursor: 'pointer',
            fontFamily: 'inherit', padding: 0,
          }}
        >
          Already have an account? Log in
        </button>
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <div style={{ fontSize: 11, color: '#4b5563', textAlign: 'center', maxWidth: 600, lineHeight: 1.6 }}>
        DA data sourced from council records and the NSW Planning Portal under{' '}
        <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer"
          style={{ color: '#4b5563', textDecoration: 'underline' }}>CC BY 4.0</a>
      </div>
    </div>
  );
}
